import { useEffect } from "react";
import { motion } from "framer-motion";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useT } from "@/i18n_extra2";

export const FaqSection = () => {
  const t = useT();
  const f = t.faq;

  useEffect(() => {
    const schema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: f.items.map((i) => ({
        "@type": "Question",
        name: i.q,
        acceptedAnswer: { "@type": "Answer", text: i.a },
      })),
    };
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "faq-jsonld";
    script.text = JSON.stringify(schema);
    document.getElementById("faq-jsonld")?.remove();
    document.head.appendChild(script);
    return () => script.remove();
  }, [f]);

  return (
    <section id="faq" className="relative py-28 sm:py-32 bg-[#F3F1EC] border-t border-black/5" data-testid="faq-section">
      <div className="max-w-5xl mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
        >
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-10 bg-[#D4AF37]" />
            <span className="text-xs font-bold tracking-[0.25em] uppercase text-[#D4AF37]">{f.label}</span>
          </div>
          <h2 className="font-display font-bold tracking-tight text-4xl sm:text-5xl leading-[1.05]">{f.title}</h2>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="mt-12"
        >
          <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
            {f.items.map((item, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border-black/10" data-testid={`faq-item-${i}`}>
                <AccordionTrigger className="text-left font-display font-semibold text-base sm:text-lg text-[#111111] hover:text-[#D4AF37] hover:no-underline py-6 transition-colors duration-300">
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm sm:text-base text-black/60 leading-relaxed pb-6 max-w-3xl">
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>
      </div>
    </section>
  );
};
